import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem"
  },
  formField: {
    marginBottom: theme.spacing(2)
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap"
  },
  infoBox: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3)
  }
}));

const GoogleCalendarConfig = ({ settings }) => {
  const classes = useStyles();
  const { update } = useCompanySettings();

  const [enabled, setEnabled] = useState("disabled");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [calendarId, setCalendarId] = useState("primary");
  const [reminderMinutes, setReminderMinutes] = useState("30");
  const [showSecret, setShowSecret] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!settings) return;

    setEnabled(settings.googleCalendarEnabled || "disabled");
    setClientId(settings.googleCalendarClientId || "");
    setClientSecret(settings.googleCalendarClientSecret || "");
    setRefreshToken(settings.googleCalendarRefreshToken || "");
    setCalendarId(settings.googleCalendarId || "primary");
    setReminderMinutes(settings.googleCalendarReminderMinutes || "30");
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ column: "googleCalendarEnabled", data: enabled });
      await update({ column: "googleCalendarClientId", data: clientId });
      await update({ column: "googleCalendarClientSecret", data: clientSecret });
      await update({ column: "googleCalendarRefreshToken", data: refreshToken });
      await update({ column: "googleCalendarId", data: calendarId || "primary" });
      await update({ column: "googleCalendarReminderMinutes", data: reminderMinutes || "30" });
      toast.success("Configuración de Google Calendar guardada.");
    } catch (_error) {
      toast.error("No se pudo guardar la configuración de Google Calendar.");
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/companySettings/test-google-calendar");
      toast.success(data?.message || "Conexión validada con Google Calendar.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "No se pudo validar la conexión con Google Calendar.");
    }
    setTesting(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/companySettings/sync-google-calendar");
      const synced = Number(data?.synced || 0);
      const removed = Number(data?.removed || 0);
      toast.success(`Sincronización completada. Activas: ${synced}. Retiradas: ${removed}.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No se pudo sincronizar con Google Calendar.");
    }
    setSyncing(false);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/companySettings/import-google-calendar", {
        calendarId: calendarId || "primary",
        daysBack: 30,
        daysForward: 180
      });
      const imported = Number(data?.imported || 0);
      const updated = Number(data?.updated || 0);
      const skipped = Number(data?.skipped || 0);
      toast.success(`Importacion completada. Nuevas: ${imported}. Actualizadas: ${updated}. Omitidas: ${skipped}.`);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No se pudieron importar los eventos de Google Calendar.");
    }
    setImporting(false);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.sectionTitle}>
        Google Calendar
      </Typography>
      <Typography className={classes.subtitle}>
        Sincronice las programaciones del CRM con un calendario de Google para que las citas queden visibles y con recordatorios.
      </Typography>

      <Box className={classes.infoBox}>
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          Datos mínimos para sincronizar:
        </Typography>
        <Typography variant="body2">1. Client ID</Typography>
        <Typography variant="body2">2. Client Secret</Typography>
        <Typography variant="body2">3. Refresh Token</Typography>
        <Typography variant="body2">4. Calendar ID (`primary` o un ID compartido)</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={enabled}
              onChange={(e) => setEnabled(e.target.value)}
              label="Estado"
            >
              <MenuItem value="enabled">Activo</MenuItem>
              <MenuItem value="disabled">Inactivo</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Calendar ID"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            helperText="Use `primary` o el ID del calendario."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Recordatorio interno (minutos antes)"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={reminderMinutes}
            onChange={(e) => setReminderMinutes(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Client ID"
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Client Secret"
            type={showSecret ? "text" : "password"}
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end">
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Refresh Token"
            type={showToken ? "text" : "password"}
            variant="outlined"
            fullWidth
            className={classes.formField}
            value={refreshToken}
            onChange={(e) => setRefreshToken(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowToken((prev) => !prev)} edge="end">
                    {showToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>
      </Grid>

      <div className={classes.buttonGroup}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Guardando..." : "Guardar configuración"}
        </Button>
        <Button
          variant="contained"
          onClick={handleTest}
          disabled={testing}
        >
          {testing ? "Probando..." : "Probar conexión"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? "Sincronizando..." : "Sincronizar programaciones"}
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={importing}
        >
          {importing ? "Importando..." : "Importar desde Google"}
        </Button>
      </div>
    </div>
  );
};

export default GoogleCalendarConfig;
